import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useMutation as useReactQueryMutation } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { NewDonorDialog } from '@/components/common/new-donor-dialog/NewDonorDialog';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { PoolAspirationsDialog } from '@/pages/opu-search/components/PoolAspirationsDialog';
import { updateAspirationStatusBatch as updateAspirationStatusBatchRest } from '@/services/chute-scheduler/api';
import { UpdateAspirationStatusPayload } from '@/services/chute-scheduler/types';
import { runNewWorkflow, WORKFLOW } from '@/utils';

import { BulkRevisionModal } from '../BulkRevisionModal';

const RevisionActions = ({
  resultsRows,
  gridRef,
  label,
  icon,
  severity,
  className,
  disabled,
  refetch,
}: {
  resultsRows: any[];
  gridRef: React.RefObject<AgGridReact>;
  label: string;
  icon: string;
  severity:
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'danger'
    | 'contrast'
    | 'help'
    | undefined;
  className?: string;
  disabled?: boolean;
  refetch?: () => void;
}) => {
  const dispatch = useDispatch();
  const { mutateAsync: updateAspirationStatusRestMutate } =
    useReactQueryMutation({
      mutationFn: (updates: UpdateAspirationStatusPayload[]) =>
        updateAspirationStatusBatchRest(updates),
    });

  const [showPoolAspirations, setShowPoolAspirations] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showNewDonorDialog, setShowNewDonorDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    action: () => void;
    message: string;
  } | null>(null);
  const overlayRef = useRef<OverlayPanel>(null);

  const [revisionComment, setRevisionComment] = useState('');
  const [revisionNumber, setRevisionNumber] = useState({
    previousRevision: '',
    currentRevision: '',
  });

  const [showBulkRevisionModal, setShowBulkRevisionModal] = useState(false);
  const [selectedDonorScheduleIds, setSelectedDonorScheduleIds] = useState<
    string[]
  >([]);

  const poolNumArray = useMemo(() => {
    return resultsRows
      ?.filter((a) => a.poolNum != null)
      ?.map((a) => Number(a.poolNum)) as number[];
  }, [resultsRows]);

  const handleAddDonorSchedule = useCallback(() => {
    window.open(`/donor-scheduling`, '_blank');
  }, []);

  const handleAddDonor = useCallback(() => {
    const selectedNodes = gridRef.current?.api?.getSelectedNodes();
    if (selectedNodes && selectedNodes.length > 0) {
      setShowNewDonorDialog(true);
    }
  }, [gridRef]);

  const handleAddSire = useCallback(() => {
    const selectedNodes = gridRef.current?.api?.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length === 0) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No row selected',
          detail: 'Please select a row',
          life: 3000,
        }),
      );
      return;
    }
    if (selectedNodes.length > 1) {
      const ids = selectedNodes.map(
        (node) => node.data.donorScheduleId as string,
      );
      setSelectedDonorScheduleIds(ids);
      setShowBulkRevisionModal(true);
      return;
    }
    const donorScheduleIds = selectedNodes.map(
      (node) => node.data.donorScheduleId,
    );

    setConfirmAction({
      action: () => {
        window.open(
          `/donor-scheduling?edit=true&revision=true&tab=semen&id=${donorScheduleIds[0]}`,
          '_blank',
        );
      },
      message:
        'Are you sure you want to open the donor schedule for adding a sire?',
    });

    setShowConfirmDialog(true);
  }, [dispatch, gridRef]);

  const handleChangeSire = useCallback(() => {
    const selectedNodes = gridRef.current?.api?.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length === 0) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No row selected',
          detail: 'Please select a row',
          life: 3000,
        }),
      );
      return;
    }
    if (selectedNodes.length > 1) {
      const ids = selectedNodes.map(
        (node) => node.data.donorScheduleId as string,
      );
      setSelectedDonorScheduleIds(ids);
      setShowBulkRevisionModal(true);
      return;
    }
    const donorScheduleIds = selectedNodes.map(
      (node) => node.data.donorScheduleId,
    );

    setConfirmAction({
      action: () => {
        window.open(
          `/donor-scheduling?edit=true&revision=true&tab=semen&id=${donorScheduleIds[0]}`,
          '_blank',
        );
      },
      message:
        'Are you sure you want to open the donor schedule for changing the sire?',
    });

    setShowConfirmDialog(true);
  }, [dispatch, gridRef]);

  const handleChangeSemenType = useCallback(() => {
    const selectedNodes = gridRef.current?.api?.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length === 0) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No row selected',
          detail: 'Please select a row',
          life: 3000,
        }),
      );
      return;
    }
    if (selectedNodes.length > 1) {
      const ids = selectedNodes.map(
        (node) => node.data.donorScheduleId as string,
      );
      setSelectedDonorScheduleIds(ids);
      setShowBulkRevisionModal(true);
      return;
    }
    const donorScheduleIds = selectedNodes.map(
      (node) => node.data.donorScheduleId,
    );

    setConfirmAction({
      action: () => {
        window.open(
          `/donor-scheduling?edit=true&revision=true&tab=semen&id=${donorScheduleIds[0]}`,
          '_blank',
        );
      },
      message:
        'Are you sure you want to open the donor schedule for changing the semen type?',
    });

    setShowConfirmDialog(true);
  }, [dispatch, gridRef]);

  const handleChangeSemenLocation = useCallback(() => {
    const selectedNodes = gridRef.current?.api?.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length === 0) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No row selected',
          detail: 'Please select a row',
          life: 3000,
        }),
      );
      return;
    }
    if (selectedNodes.length > 1) {
      const ids = selectedNodes.map(
        (node) => node.data.donorScheduleId as string,
      );
      setSelectedDonorScheduleIds(ids);
      setShowBulkRevisionModal(true);
      return;
    }
    const donorScheduleIds = selectedNodes.map(
      (node) => node.data.donorScheduleId,
    );

    setConfirmAction({
      action: () => {
        window.open(
          `/donor-scheduling?edit=true&revision=true&tab=semen&id=${donorScheduleIds[0]}`,
          '_blank',
        );
      },
      message:
        'Are you sure you want to open the donor schedule for changing the semen location/owner/code?',
    });

    setShowConfirmDialog(true);
  }, [dispatch, gridRef]);

  const handleSplit = useCallback(async () => {
    const recordIds =
      gridRef.current?.api
        ?.getSelectedNodes()
        .map((node) => ({ RecordId: node.data.donorScheduleId })) ?? [];

    try {
      await runNewWorkflow(recordIds, WORKFLOW.Split.id);
      refetch?.();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Workflow Executed',
          detail:
            "Split workflow completed successfully. Workflows don't run right away. Please refresh the page to see how they are being created.",
          life: ToastLife.MEDIUM,
        }),
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Workflow Error',
          detail: `Failed to execute Split workflow. Please try again. Error: ${errorMessage}`,
          life: ToastLife.LONG,
        }),
      );
    }
  }, [dispatch, gridRef, refetch]);

  const handlePoolingDonors = () => {
    setShowPoolAspirations(true);
  };

  const handleDidNotAspirate = useCallback(async () => {
    const selectedNodes = gridRef.current?.api?.getSelectedNodes();
    const updates = selectedNodes?.map((node) => {
      return {
        aspirationId: node.data.aspirationId,
        donorScheduleId: node.data.donorScheduleId,
        targetStatus: 'Did Not Aspirate',
        revisionComment: `${node.data.donorName} was not aspirated. `,
      };
    }) as UpdateAspirationStatusPayload[];

    if (selectedNodes && selectedNodes.length > 0) {
      await updateAspirationStatusRestMutate(updates);
      refetch?.();
    }
  }, [gridRef, updateAspirationStatusRestMutate, refetch]);

  const handleDoNotFertilize = useCallback(async () => {
    const selectedNodes = gridRef.current?.api?.getSelectedNodes();

    const updates = selectedNodes?.map((node) => {
      return {
        aspirationId: node.data.aspirationId,
        donorScheduleId: node.data.donorScheduleId,
        targetStatus: 'Do Not Fertilize',
        revisionComment: `${node.data.donorName} is not to be fertilized. `,
      };
    }) as UpdateAspirationStatusPayload[];

    if (selectedNodes && selectedNodes.length > 0) {
      await updateAspirationStatusRestMutate(updates);
      refetch?.();
    } else {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No row selected',
          detail: 'Please select a row',
          life: 3000,
        }),
      );
    }
  }, [dispatch, gridRef, updateAspirationStatusRestMutate, refetch]);

  const handleConfirmAction = () => {
    if (confirmAction) {
      confirmAction.action();
    }
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  const handleRejectAction = () => {
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };
  const revisionActions = useMemo(() => {
    return [
      {
        id: 'add-donor-schedule',
        label: 'Add Donor Schedule',
        icon: 'pi pi-plus-circle',
        command: handleAddDonorSchedule,
        tooltip:
          'Will bring up donor scheduling app to add a new donor schedule',
      },
      {
        id: 'add-donor',
        label: 'Add Donor',
        icon: 'pi pi-plus-circle',
        command: handleAddDonor,
        tooltip:
          'Will bring up donor scheduling app to add a new donor to the selected schedule.',
      },
      {
        id: 'add-sire',
        label: 'Add Sire',
        icon: 'pi pi-plus-circle',
        command: handleAddSire,
        tooltip:
          'Will take you to the donor scheduling app to add a sire to the selected schedule.',
      },
      {
        id: 'change-sire',
        label: 'Change Sire',
        icon: 'pi pi-arrows-h',
        command: handleChangeSire,
        tooltip:
          'Will take you to the donor scheduling app to change the sire for the selected schedule.',
      },
      {
        id: 'change-semen-type',
        label: 'Change Semen Type',
        icon: 'pi pi-tag',
        command: handleChangeSemenType,
        tooltip: `Will take you to the donor scheduling app to change
          the semen type for the selected schedule.`,
      },
      {
        id: 'change-semen-location',
        label: 'Change Semen Location/Owner/Code',
        icon: 'pi pi-map-marker',
        command: handleChangeSemenLocation,
        tooltip: `Will take you to the donor scheduling app to change
          the semen location, owner, or code for the selected schedule.`,
      },
      {
        id: 'split',
        label: 'Split',
        icon: 'pi pi-share-alt',
        command: handleSplit,
        tooltip: `Will Run the Split workflow. You will need to add revision comments
          to the newly added donor schedule and the original donor schedule.`,
      },
      {
        id: 'pooling',
        label: 'Pooling',
        icon: 'pi pi-users',
        command: handlePoolingDonors,
        tooltip: `Will bring up the Pool Aspirations dialog to pool donors for aspiration scheduling.
        Will automatically assign revision number and appropriate revision comment.`,
      },
      {
        id: 'did-not-aspirate',
        label: 'Did Not Aspirate',
        icon: 'pi pi-times-circle',
        command: handleDidNotAspirate,
        tooltip: `Will mark aspiration as Did Not Aspirate.
          Will automatically assign revision number and appropriate revision comment.`,
      },
      {
        id: 'do-not-fertilize',
        label: 'Do Not Fertilize',
        icon: 'pi pi-ban',
        command: handleDoNotFertilize,
        tooltip: `Will mark aspiration as Do Not Fertilize.
          Will automatically assign revision number and appropriate revision comment.`,
      },
    ];
  }, [
    handleAddDonorSchedule,
    handleAddDonor,
    handleAddSire,
    handleChangeSemenLocation,
    handleChangeSemenType,
    handleChangeSire,
    handleDidNotAspirate,
    handleDoNotFertilize,
    handlePoolingDonors,
    handleSplit,
  ]);

  return (
    <>
      <Button
        label={label}
        icon={icon}
        severity={severity}
        className={className}
        disabled={disabled}
        onClick={(event) => overlayRef.current?.toggle(event)}
        aria-haspopup
      />
      <OverlayPanel ref={overlayRef} className="p-menu-overlay">
        <div className="p-menu-list p-menu p-component">
          {revisionActions.map((action) => (
            <div key={action.id} className="p-menuitem">
              <button
                id={action.id}
                className="p-menuitem-link p-menuitem-content flex h-10 items-center gap-2 border-none bg-transparent"
                onClick={() => {
                  action.command();
                  overlayRef.current?.hide();
                }}
              >
                <span className="p-menuitem-icon pi pi-fw">
                  <i className={action.icon}></i>
                </span>
                <span className="p-menuitem-text">{action.label}</span>
              </button>
              <Tooltip
                target={`#${action.id}`}
                content={action.tooltip}
                position="left"
              />
            </div>
          ))}
        </div>
      </OverlayPanel>
      {showPoolAspirations && (
        <Dialog
          header="Pool Aspirations"
          visible={showPoolAspirations}
          onHide={() => setShowPoolAspirations(false)}
        >
          <PoolAspirationsDialog
            setShowPoolAspirations={setShowPoolAspirations}
            refetch={() => {}}
            poolNumArray={poolNumArray}
          />
        </Dialog>
      )}
      <ConfirmDialog
        visible={showConfirmDialog}
        onHide={handleRejectAction}
        message={confirmAction?.message || 'Are you sure you want to proceed?'}
        header="Confirm Action"
        icon="pi pi-exclamation-triangle"
        accept={handleConfirmAction}
        reject={handleRejectAction}
        acceptLabel="Yes"
        rejectLabel="No"
      />
      <NewDonorDialog
        visible={showNewDonorDialog}
        onHide={() => setShowNewDonorDialog(false)}
        gridRef={gridRef}
        revisionComment={revisionComment}
        setRevisionComment={setRevisionComment}
        revisionNumber={revisionNumber}
        setRevisionNumber={setRevisionNumber}
        updateRevisionsAfterSave={true}
      />
      <BulkRevisionModal
        visible={showBulkRevisionModal}
        onHide={() => setShowBulkRevisionModal(false)}
        donorScheduleIds={selectedDonorScheduleIds}
        onSaveSuccess={refetch}
      />
    </>
  );
};

export default RevisionActions;
